import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { loadColleagueOptions } from '../utils/loadColleagues';
import { useParams, useNavigate } from 'react-router-dom';
import { taskApi } from '../api';
import { taskHandoffApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Alert, PriorityBadge, StatusBadge, Spinner } from '../components/ui';

const STATUS_TRANSITIONS = {
  PENDING:     ['IN_PROGRESS', 'ON_HOLD'],
  IN_PROGRESS: ['DONE', 'ON_HOLD'],
  ON_HOLD:     ['IN_PROGRESS'],
  DONE:        [],
  OVERDUE:     ['IN_PROGRESS', 'ON_HOLD'],
};

const STATUS_LABELS = {
  IN_PROGRESS: 'Start',
  ON_HOLD:     'Put On Hold',
  DONE:        'Mark Done',
  PENDING:     'Reset to Pending',
};

function transitionLabel(currentStatus, targetStatus) {
  if (currentStatus === 'ON_HOLD' && targetStatus === 'IN_PROGRESS') {
    return 'Resume (Off Hold)';
  }
  return STATUS_LABELS[targetStatus] || targetStatus.replace(/_/g, ' ');
}


export default function TaskDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [task,         setTask]         = useState(null);
  const [notes,        setNotes]        = useState([]);
  const [history,      setHistory]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [activeTab,    setActiveTab]    = useState('notes'); // notes | history
  const [noteText,     setNoteText]     = useState('');
  const [isPrivate,    setIsPrivate]    = useState(false);
  const [addingNote,   setAddingNote]   = useState(false);
  const [statusModal,  setStatusModal]  = useState(null);
  const [onHoldReason, setOnHoldReason] = useState('');
  const [extModal,     setExtModal]     = useState(false);
  const [extDate,      setExtDate]      = useState('');
  const [extReason,    setExtReason]    = useState('');
  const [actionError,  setActionError]  = useState('');
  const [unassignModal,  setUnassignModal]   = useState(false);
  const [unassignReason, setUnassignReason]  = useState('');
  const [handoffModal,   setHandoffModal]    = useState(false);
  const [handoffToId,    setHandoffToId]     = useState('');
  const [handoffReason,  setHandoffReason]   = useState('');
  const [teamMembers,    setTeamMembers]     = useState([]);
  const [colleagueError, setColleagueError]   = useState('');
  const [assignModal,    setAssignModal]     = useState(false);
  const [assignToId,     setAssignToId]      = useState('');
  const [assigning,      setAssigning]       = useState(false);

  const isSuperAdmin   = user?.role === 'SUPER_ADMIN';
  const isAdminOrAbove = isSuperAdmin || user?.role === 'ADMIN';
  const isAssignee     = task?.assignedTo?.id === user?.id || false;
  const canRequestExtension = user?.role === 'EMPLOYEE' && isAssignee && task?.status !== 'DONE';

  const load = useCallback((refreshHistory = false) => {
    const calls = [taskApi.getById(id), taskApi.getNotes(id)];
    if (refreshHistory) calls.push(taskApi.getHistory(id));
    Promise.all(calls)
      .then(([t, n, h]) => {
        setTask(t.data.data);
        setNotes(n.data.data || []);
        if (h) setHistory(h.data.data || []);
      })
      .catch(() => setError('Failed to load task'))
      .finally(() => setLoading(false));
  }, [id]);

  // Colleagues for handoff (employees → /api/team) or assign (admins → /api/users)
  useEffect(() => {
    setColleagueError('');
    loadColleagueOptions({ user, isAdminOrAbove, isSuperAdmin })
      .then(setTeamMembers)
      .catch(() => setColleagueError('Could not load colleague list'));
  }, [user?.id, isAdminOrAbove, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleSelfUnassign = async () => {
    if (!unassignReason.trim()) { setActionError('Please provide a reason'); return; }
    setActionError('');
    try {
      await taskHandoffApi.selfUnassign(id, unassignReason);
      setUnassignModal(false); setUnassignReason('');
      navigate('/tasks');
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to unassign');
    }
  };

  const handleAssign = async () => {
    if (!assignToId) { setActionError('Select an employee'); return; }
    setActionError(''); setAssigning(true);
    try {
      await taskApi.update(id, { assignedToId: assignToId });
      setAssignModal(false); setAssignToId('');
      load(true);
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to assign task');
    } finally {
      setAssigning(false);
    }
  };

  const handleHandoff = async () => {
    if (!handoffToId)           { setActionError('Select an employee'); return; }
    if (!handoffReason.trim())  { setActionError('Reason is required'); return; }
    setActionError('');
    try {
      await taskHandoffApi.handoff(id, handoffToId, handoffReason);
      setHandoffModal(false); setHandoffToId(''); setHandoffReason('');
      // No longer assignee — reloading this task returns 403 and shows a false error.
      navigate('/tasks', { state: { flash: 'Task handed off successfully.' } });
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to hand off task');
    }
  };

  const loadHistory = () => {
    taskApi.getHistory(id)
      .then(r => setHistory(r.data.data || []))
      .catch(() => {});
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'history') loadHistory();
  };

  const submitStatus = async () => {
    if (statusModal === 'ON_HOLD' && !onHoldReason.trim()) {
      setActionError('Reason is required when putting on hold'); return;
    }
    setActionError('');
    try {
      await taskApi.updateStatus(id, { status: statusModal, onHoldReason });
      setStatusModal(null); setOnHoldReason('');
      load(true);
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to update status');
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await taskApi.addNote(id, { content: noteText, privateNote: isPrivate });
      setNoteText(''); setIsPrivate(false);
      const r = await taskApi.getNotes(id);
      setNotes(r.data.data || []);
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const submitExtension = async () => {
    if (!extDate || !extReason.trim()) { setActionError('Date and reason are required'); return; }
    setActionError('');
    try {
      await taskApi.requestExtension(id, { requestedDeadline: extDate, reason: extReason });
      setExtModal(false); setExtDate(''); setExtReason('');
      setActionError('');
      alert('Extension request submitted');
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to submit extension request');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task? It will be archived.')) return;
    try {
      await taskApi.delete(id);
      navigate('/tasks');
    } catch (e) {
      setActionError(e.response?.data?.message || 'Failed to delete task');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner size={28} /></div>
  );
  if (error) return (
    <div style={{ padding: '24px' }}><Alert type="error" message={error} /></div>
  );
  if (!task) return null;

  const transitions = STATUS_TRANSITIONS[task.status] || [];
  const minDate     = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  return (
    <div style={{ padding: '24px', maxWidth: '720px' }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={S.backBtn}>← Back</button>

      {/* Header */}
      <div style={{ marginTop: '12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', flex: 1 }}>{task.title}</h1>
          <div style={{ display: 'flex', gap: '6px' }}>
            <PriorityBadge priority={task.priority} />
            <StatusBadge   status={task.status}     />
          </div>
        </div>

        {task.wasLate && task.status === 'DONE' && (
          <div style={{ marginTop: '8px', padding: '6px 12px', background: '#FFFBEB', borderRadius: 'var(--radius-sm)', fontSize: '12px', color: '#D69E2E', display: 'inline-block' }}>
            Completed late ({task.daysOverdue}d after deadline)
          </div>
        )}

        {task.status === 'OVERDUE' && (
          <div style={{ marginTop: '8px', padding: '6px 12px', background: 'var(--color-danger-bg)', borderRadius: 'var(--radius-sm)', fontSize: '12px', color: 'var(--color-danger)', display: 'inline-block' }}>
            Deadline passed {task.daysOverdue} day{task.daysOverdue !== 1 ? 's' : ''} ago — needs attention
          </div>
        )}
      </div>

      {actionError && <Alert type="error" message={actionError} />}

      {/* Meta */}
      <div style={S.metaGrid}>
        <MetaItem label="Assigned to"   value={task.assignedTo?.name  || 'Unassigned'} />
        <MetaItem label="Assigned by"   value={task.assignedBy?.name  || '—'}           />
        <MetaItem label="Department"    value={task.department?.name  || '—'}           />
        <MetaItem label="Deadline"      value={task.deadline           || '—'}           />
        <MetaItem label="Created"       value={task.createdAt ? fmtDate(task.createdAt) : '—'} />
        {task.completedAt && (
          <MetaItem label="Completed" value={fmtDate(task.completedAt)} />
        )}
      </div>

      {task.description && (
        <div style={S.descBox}>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '6px' }}>Description</p>
          <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--color-text)' }}>{task.description}</p>
        </div>
      )}

      {task.onHoldReason && (
        <div style={{ ...S.descBox, borderLeft: '3px solid var(--color-warning)', background: 'var(--color-warning-bg)' }}>
          <p style={{ fontSize: '12px', color: 'var(--color-warning)', fontWeight: 600, marginBottom: '4px' }}>ON HOLD</p>
          <p style={{ fontSize: '14px', color: 'var(--color-text)', marginBottom: task.status === 'ON_HOLD' ? '12px' : 0 }}>{task.onHoldReason}</p>
          {task.status === 'ON_HOLD' && (
            <Btn size="sm" onClick={() => { setStatusModal('IN_PROGRESS'); setActionError(''); }}>
              Resume (Off Hold)
            </Btn>
          )}
        </div>
      )}

      {task.tags?.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {task.tags.map(t => (
            <span key={t} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)' }}>{t}</span>
          ))}
        </div>
      )}

      {/* Status actions */}
      {transitions.length > 0 && (
        <div style={S.actionRow}>
          <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Update Status:</p>
          {transitions.map(s => (
            <Btn key={s} size="sm"
              variant={s === 'DONE' ? 'primary' : s === 'ON_HOLD' ? 'secondary' : 'secondary'}
              onClick={() => { setStatusModal(s); setActionError(''); }}
            >
              {transitionLabel(task.status, s)}
            </Btn>
          ))}
        </div>
      )}

      {/* Admin reopen */}
      {isAdminOrAbove && task.status === 'DONE' && (
        <div style={S.actionRow}>
          <Btn size="sm" variant="ghost" onClick={() => { setStatusModal('IN_PROGRESS'); setActionError(''); }}>
            Reopen Task
          </Btn>
        </div>
      )}

      {/* Assign when unassigned */}
      {isAdminOrAbove && !task.assignedTo && task.status !== 'DONE' && (
        <div style={{ ...S.actionRow, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <p style={{ fontSize: '13px', color: '#1D4ED8', flex: 1 }}>
            This task has no assignee yet.
          </p>
          <Btn size="sm" onClick={() => { setAssignModal(true); setActionError(''); }}>
            Assign Employee
          </Btn>
        </div>
      )}

      {/* Secondary actions */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {canRequestExtension && (
          <Btn size="sm" variant="ghost" onClick={() => { setExtModal(true); setActionError(''); }}>
            Request Deadline Extension
          </Btn>
        )}
        {/* Employee: self-unassign and handoff */}
        {!isAdminOrAbove && isAssignee && task?.status !== 'DONE' && (
          <>
            <Btn size="sm" variant="ghost" onClick={() => { setHandoffModal(true); setActionError(''); }}>
              Hand Off to Colleague
            </Btn>
            <Btn size="sm" variant="ghost" onClick={() => { setUnassignModal(true); setActionError(''); }}>
              Unassign Myself
            </Btn>
          </>
        )}
        {isAdminOrAbove && (
          <>
            <Btn size="sm" variant="ghost" onClick={() => navigate(`/tasks/${id}/edit`)}>Edit Task</Btn>
            <Btn size="sm" variant="ghost" onClick={async () => { await taskApi.duplicate(id); navigate('/pool'); }}>Duplicate to Pool</Btn>
            <Btn size="sm" variant="danger" onClick={handleDelete}>Delete</Btn>
          </>
        )}
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {['notes', 'history'].map(tab => (
          <button key={tab} onClick={() => handleTabChange(tab)} style={{
            ...S.tab,
            borderBottom: `2px solid ${activeTab === tab ? 'var(--color-primary)' : 'transparent'}`,
            color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
            fontWeight: activeTab === tab ? 600 : 400,
          }}>
            {tab === 'notes' ? 'Progress Notes' : 'Task History'}
          </button>
        ))}
      </div>

      {/* Notes tab */}
      {activeTab === 'notes' && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {notes.length === 0 ? (
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', padding: '16px 0' }}>No notes yet.</p>
            ) : (
              notes.map(n => (
                <div key={n.id} style={{
                  padding: '12px 14px', borderRadius: 'var(--radius-md)',
                  background: n.privateNote ? '#FFFBEB' : 'var(--color-surface)',
                  border: `1px solid ${n.privateNote ? '#FAF089' : 'var(--color-border)'}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>{n.author?.name}</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {n.privateNote && (
                        <span style={{ fontSize: '10px', background: '#FEFCBF', color: '#744210', padding: '1px 6px', borderRadius: '999px', fontWeight: 600 }}>PRIVATE</span>
                      )}
                      <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', lineHeight: 1.5 }}>{n.content}</p>
                </div>
              ))
            )}
          </div>

          {/* Add note */}
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '16px' }}>
            <textarea
              placeholder="Add a progress note..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', resize: 'vertical', marginBottom: '8px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} />
                Private note (only visible to you)
              </label>
              <Btn size="sm" onClick={submitNote} loading={addingNote} disabled={!noteText.trim()}>
                Add Note
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {history.length === 0 ? (
            <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', padding: '16px 0' }}>No history yet.</p>
          ) : (
            history.map(h => (
              <div key={h.id} style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <span>
                    <strong>{h.changedBy?.name}</strong>
                    {' — '}
                    <span style={{ color: 'var(--color-primary)' }}>{h.changeType.replace(/_/g, ' ')}</span>
                    {h.oldValue && h.newValue && ` · ${h.oldValue} → ${h.newValue}`}
                    {h.note && <span style={{ color: 'var(--color-text-muted)' }}> · {h.note}</span>}
                  </span>
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>{timeAgo(h.timestamp)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Status modal */}
      {statusModal && (
        <Modal title={`Update Status: ${statusModal.replace('_', ' ')}`} onClose={() => setStatusModal(null)}>
          {actionError && <Alert type="error" message={actionError} />}
          {statusModal === 'ON_HOLD' && (
            <div style={{ marginTop: '12px' }}>
              <label style={S.modalLabel}>Reason for putting on hold *</label>
              <textarea
                value={onHoldReason}
                onChange={e => setOnHoldReason(e.target.value)}
                placeholder="Explain why this task is on hold..."
                rows={3}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', marginTop: '6px', resize: 'vertical' }}
              />
            </div>
          )}
          {statusModal === 'IN_PROGRESS' && task.status === 'DONE' && (
            <div style={{ marginTop: '12px' }}>
              <label style={S.modalLabel}>Reason for reopening</label>
              <textarea
                value={onHoldReason}
                onChange={e => setOnHoldReason(e.target.value)}
                placeholder="Why is this task being reopened?"
                rows={2}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', marginTop: '6px', resize: 'vertical' }}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setStatusModal(null)}>Cancel</Btn>
            <Btn onClick={submitStatus}>Confirm</Btn>
          </div>
        </Modal>
      )}

      {/* Extension modal */}
      {extModal && (
        <Modal title="Request Deadline Extension" onClose={() => setExtModal(false)}>
          {actionError && <Alert type="error" message={actionError} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
            <div>
              <label style={S.modalLabel}>Requested new deadline *</label>
              <input type="date" min={minDate} value={extDate} onChange={e => setExtDate(e.target.value)}
                style={{ ...S.modalInput, marginTop: '6px' }} />
            </div>
            <div>
              <label style={S.modalLabel}>Reason *</label>
              <textarea value={extReason} onChange={e => setExtReason(e.target.value)}
                placeholder="Explain why you need more time..."
                rows={3} style={{ ...S.modalInput, resize: 'vertical', marginTop: '6px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setExtModal(false)}>Cancel</Btn>
              <Btn onClick={submitExtension}>Submit Request</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Self-unassign modal */}
      {unassignModal && (
        <Modal title="Unassign Myself" onClose={() => setUnassignModal(false)}>
          {actionError && <Alert type="error" message={actionError} />}
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
            This task will be returned to the Common Pool. Your Admin will see it there.
            Every unassignment is permanently logged.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={S.modalLabel}>Reason for unassigning *</label>
            <textarea
              value={unassignReason}
              onChange={e => setUnassignReason(e.target.value)}
              placeholder='e.g. "Assigned myself by mistake" or "This task belongs to another team"'
              rows={3}
              style={{ ...S.modalInput, resize: 'vertical', marginTop: '6px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setUnassignModal(false)}>Cancel</Btn>
            <Btn variant="danger" onClick={handleSelfUnassign} disabled={!unassignReason.trim()}>
              Confirm Unassign
            </Btn>
          </div>
        </Modal>
      )}

      {/* Assign modal */}
      {assignModal && (
        <Modal title="Assign Task" onClose={() => setAssignModal(false)}>
          {actionError && <Alert type="error" message={actionError} />}
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
            Choose who should own this task. They will be notified immediately.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={S.modalLabel}>Employee *</label>
            <select value={assignToId} onChange={e => setAssignToId(e.target.value)}
              style={{ ...S.modalInput, marginTop: '6px' }}>
              <option value="">— Select employee —</option>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.employeeId}){m.away ? ' · Away' : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setAssignModal(false)}>Cancel</Btn>
            <Btn onClick={handleAssign} loading={assigning} disabled={!assignToId}>Assign</Btn>
          </div>
        </Modal>
      )}

      {/* Handoff modal */}
      {handoffModal && (
        <Modal title="Hand Off to Colleague" onClose={() => setHandoffModal(false)}>
          {actionError && <Alert type="error" message={actionError} />}
          {colleagueError && <Alert type="error" message={colleagueError} />}
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
            Transfer this task to another employee. They will be notified immediately.
            The handoff reason is permanently logged.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={S.modalLabel}>Hand off to *</label>
              <select value={handoffToId} onChange={e => setHandoffToId(e.target.value)}
                style={{ ...S.modalInput, marginTop: '6px' }}>
                <option value="">— Select colleague —</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.employeeId}){m.away ? ' — Away' : ''}
                  </option>
                ))}
              </select>
              {!colleagueError && teamMembers.length === 0 && (
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px' }}>
                  No colleagues in your department yet. Ask your admin to assign teammates to the same department.
                </p>
              )}
              {teamMembers.find(m => m.id === handoffToId)?.away && (
                <p style={{ fontSize: '12px', color: 'var(--color-warning)', marginTop: '4px' }}>
                  ⚠️ This person is currently Away. You can still hand off but they may not see it immediately.
                </p>
              )}
            </div>
            <div>
              <label style={S.modalLabel}>Reason for handoff *</label>
              <textarea
                value={handoffReason}
                onChange={e => setHandoffReason(e.target.value)}
                placeholder="e.g. This task needs expertise in X, passing to the right person"
                rows={3}
                style={{ ...S.modalInput, resize: 'vertical', marginTop: '6px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setHandoffModal(false)}>Cancel</Btn>
              <Btn onClick={handleHandoff} disabled={!handoffToId || !handoffReason.trim()}>
                Hand Off Task
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</p>
      <p style={{ fontSize: '14px', color: 'var(--color-text)', fontWeight: 500 }}>{value}</p>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-primary)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--color-text-muted)' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const S = {
  backBtn:    { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--color-text-muted)', padding: 0 },
  metaGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px', padding: '16px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)' },
  descBox:    { padding: '14px', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', marginBottom: '16px' },
  actionRow:  { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '12px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', marginBottom: '12px' },
  tabs:       { display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '16px' },
  tab:        { padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s' },
  modalLabel: { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
  modalInput: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px' },
};
